"use client";

import { useState } from "react";
import { ArrowRight, Clock, Copy, FileText, Sparkles, Video } from "lucide-react";
import { motion } from "motion/react";
import { MeshGradient } from "@paper-design/shaders-react";
import { useLocale, useTranslations } from "next-intl";
import { Toaster, toast } from "sonner";
import { SectionEyebrow, SectionTitle } from "../section-eyebrow";
import { ScrollReveal } from "../scroll-reveal";
import { Magnetic } from "../magnetic";

const EMAIL = "arielchelly9@gmail.com";

const CARD_ICONS = [Clock, Video, FileText] as const;

export function Contact() {
  const t = useTranslations("contact");
  const locale = useLocale();
  const [copied, setCopied] = useState(false);

  const subject = t("mailto.subject");
  const body = t("mailto.body");
  const mailto = `mailto:${EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  const cards = t.raw("cards") as Array<{ title: string; body: string }>;

  async function copyEmail() {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      toast.success(t("copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Clipboard unavailable");
    }
  }

  return (
    <section
      id="contact"
      data-no-divider
      className="relative isolate overflow-hidden py-24 sm:py-32 lg:py-40"
    >
      {/* Mesh gradient en blanc — shaders WebGL avec un mask vertical qui
          fait FONDRE organiquement le mesh dans le fond du site en haut et
          en bas. Plus aucune frontière visible, juste une apparition
          progressive. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-20"
        style={{
          maskImage:
            "linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)",
        }}
      >
        <MeshGradient
          className="h-full w-full"
          colors={["#ffffff", "#f8fafc", "#e2e8f0", "#cbd5e1"]}
          speed={0.5}
          distortion={0.85}
          swirl={0.2}
        />
      </div>

      {/* Halos blancs respirants — donnent une "atmosphère" à la zone
          centrale. Animation lente et infinie pour que la transition soit
          jamais figée. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <motion.div
          className="absolute left-1/4 top-1/4 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/65 blur-3xl"
          animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.75, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute right-1/4 bottom-1/3 h-56 w-56 translate-x-1/2 rounded-full bg-aurora-cyan/15 blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 9, delay: 1.2, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/45 blur-3xl"
          animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.55, 0.35] }}
          transition={{ duration: 11, delay: 0.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Bande de blur backdrop qui se déplace lentement à la frontière
          haute/basse — adoucit visuellement la couture entre les sections
          adjacentes, sans bord visible. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-32 sm:h-48"
        style={{
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          maskImage:
            "linear-gradient(to bottom, black 0%, black 30%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, black 0%, black 30%, transparent 100%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 h-32 sm:h-48"
        style={{
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          maskImage:
            "linear-gradient(to top, black 0%, black 30%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to top, black 0%, black 30%, transparent 100%)",
        }}
      />

      <Toaster richColors closeButton position="bottom-right" dir={locale === "he" ? "rtl" : "ltr"} />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <ScrollReveal as="header" className="flex flex-col items-center gap-5 text-center">
          <div className="flex items-center gap-2">
            <SectionEyebrow>{t("eyebrow")}</SectionEyebrow>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80">
              {t("annotation")}
            </span>
          </div>
          <SectionTitle text={t("title")} italicLastWords={2} />
          <p data-blend className="max-w-xl text-base sm:text-lg">
            {t("subtitle")}
          </p>
        </ScrollReveal>

        {/* Big mailto CTA */}
        <ScrollReveal delay={0.1} className="mt-10 flex justify-center">
          <Magnetic strength={0.28} className="w-full sm:w-auto">
            <a
              href={mailto}
              // Accessible name must contain the visible text (`Nous écrire
              // par email`) so it doesn't trip the
              // `label-content-name-mismatch` audit. We prepend it to the
              // descriptive aria-label rather than dropping it — the longer
              // text still helps screen-reader users understand the action.
              aria-label={`${t("ctaPrimary")} — ${t("ctaPrimaryAria")}`}
              className="group relative inline-flex h-14 w-full items-center justify-center gap-3 overflow-hidden rounded-full bg-primary px-8 text-base font-medium text-primary-foreground shadow-[0_18px_40px_-12px_var(--aurora-cyan)] transition-all duration-300 hover:shadow-[0_22px_50px_-10px_var(--aurora-cyan)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-auto"
            >
              <span
                aria-hidden
                className="absolute inset-0 -z-10 bg-gradient-to-r from-aurora-cyan/0 via-aurora-cyan/40 to-aurora-cyan/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
              />
              <Sparkles className="size-5" aria-hidden />
              {t("ctaPrimary")}
              <ArrowRight className="size-5 transition-transform duration-300 group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
            </a>
          </Magnetic>
        </ScrollReveal>

        {/* Email in clear + copy */}
        <ScrollReveal delay={0.18} className="mt-6 flex flex-wrap items-center justify-center gap-2 text-sm text-muted-foreground">
          <span>{t("orWriteDirect")}</span>
          <a
            href={`mailto:${EMAIL}`}
            className="link-underline select-all font-medium text-foreground transition-colors hover:text-aurora-cyan"
          >
            {EMAIL}
          </a>
          <button
            type="button"
            onClick={copyEmail}
            aria-label={t("copyEmail")}
            className="inline-flex size-11 items-center justify-center rounded-full border border-border/60 bg-background text-muted-foreground transition-colors hover:border-aurora-cyan/50 hover:text-aurora-cyan focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aurora-cyan/40"
          >
            <Copy className={`size-3.5 transition-opacity ${copied ? "opacity-40" : ""}`} aria-hidden />
          </button>
        </ScrollReveal>

        {/* Reassurance cards */}
        <ScrollReveal delay={0.26} className="mt-14 grid gap-4 sm:grid-cols-3">
          {cards.map((card, i) => {
            const Icon = CARD_ICONS[i] ?? Clock;
            return (
              <article
                key={card.title}
                className="group flex flex-col gap-3 rounded-2xl border border-border/60 bg-card p-5 transition-colors hover:border-aurora-cyan/40"
              >
                <div className="relative flex size-10 items-center justify-center rounded-full border border-border/60 bg-background text-foreground/80 transition-colors group-hover:border-aurora-cyan/50 group-hover:text-aurora-cyan">
                  <Icon className="size-4" strokeWidth={1.6} aria-hidden />
                </div>
                <h3 className="font-headline text-base font-medium text-foreground">
                  {card.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {card.body}
                </p>
              </article>
            );
          })}
        </ScrollReveal>
      </div>
    </section>
  );
}
